import {announce} from '@github-ui/aria-live'
import {keepPreviousData, useQuery, useQueryClient} from '@github-ui/react-query'
import {useDebounce} from '@github-ui/use-debounce'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import type {SimilarIssue} from '../SimilarIssues'
import type {IssueCreateContext} from '../utils/option-config'

export type SimilarIssueUpdate = {
  nodeId: string
  title?: string
  state?: SimilarIssue['state']
  stateReason?: SimilarIssue['stateReason']
}

export type UseSimilarIssuesOptions = {
  repoOwner: string
  repoName: string
  title: string
  body: string
  isSimilarIssuesEnabled: boolean
  context: IssueCreateContext
}

export type UseSimilarIssuesResult = {
  items: SimilarIssue[]
  isFetching: boolean
  isError: boolean
  isExpanded: boolean
  setIsExpanded: (expanded: boolean) => void
  /** Update a similar issue in the cache. Used by subscription handlers. */
  updateIssue: (update: SimilarIssueUpdate) => void
  /** Reset state for a new issue creation session. Collapses section and resets feedback. */
  resetForNewSession: () => void
  /** Key to pass to SimilarIssues feedbackResetKey prop to reset feedback state. Incremented on resetForNewSession. */
  feedbackResetKey: number
}

const DEFAULT_MIN_QUERY_LENGTH = 20
const DEFAULT_DEBOUNCE_MS = 1500

export function useSimilarIssues({
  repoOwner,
  repoName,
  title,
  body,
  isSimilarIssuesEnabled,
  context,
}: UseSimilarIssuesOptions): UseSimilarIssuesResult {
  const queryClient = useQueryClient()
  const [isExpandedInternal, setIsExpandedInternal] = useState(false)
  const [feedbackResetKey, setFeedbackResetKey] = useState(0)
  const previousCountRef = useRef<number | null>(null)

  const [debouncedTitle, setDebouncedTitle] = useState(title.trim())
  const [debouncedBody, setDebouncedBody] = useState(body.trim())

  const debounceQueryTitleUpdate = useDebounce((value: string) => setDebouncedTitle(value.trim()), DEFAULT_DEBOUNCE_MS)
  const debounceQueryBodyUpdate = useDebounce((value: string) => setDebouncedBody(value.trim()), DEFAULT_DEBOUNCE_MS)

  // When expanding the section for the first time, no debounce necessary.
  const setIsExpanded = useCallback(
    (expanded: boolean) => {
      if (!isSimilarIssuesEnabled) return
      if (expanded) {
        setDebouncedTitle(title.trim())
        setDebouncedBody(body.trim())
      }
      setIsExpandedInternal(expanded)
    },
    [isSimilarIssuesEnabled, title, body],
  )

  // When the section is already expanded, debounce fetch to avoid fetching on every keystroke
  useEffect(() => {
    if (!isSimilarIssuesEnabled) return
    if (isExpandedInternal) {
      debounceQueryTitleUpdate(title.trim())
    }
  }, [isSimilarIssuesEnabled, title, isExpandedInternal, debounceQueryTitleUpdate])
  useEffect(() => {
    if (!isSimilarIssuesEnabled) return
    if (isExpandedInternal) {
      debounceQueryBodyUpdate(body.trim())
    }
  }, [isSimilarIssuesEnabled, body, isExpandedInternal, debounceQueryBodyUpdate])
  const isQueryValid = debouncedTitle.length + debouncedBody.length >= DEFAULT_MIN_QUERY_LENGTH

  // Memoize queryKey for use in updateIssue callback
  const queryKey = useMemo(
    () => ['similar-issues', repoOwner, repoName, debouncedTitle, debouncedBody, context] as const,
    [repoOwner, repoName, debouncedTitle, debouncedBody, context],
  )

  const {data, isFetching, isError} = useQuery({
    queryKey,
    queryFn: async ({signal}) => {
      const response = await verifiedFetchJSON(`/${repoOwner}/${repoName}/issues/semantically_similar`, {
        method: 'POST',
        body: {
          search: {
            title: debouncedTitle,
            body: debouncedBody,
            page: 1,
            per_page: 5,
            context,
          },
        },
        signal,
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch similar issues: ${response.status}`)
      }
      const json = await response.json()
      // Response shape: { similar_issues: [{ issue: { id, node_id, number, title, state, ... } }] }
      const similarIssues = json.similar_issues ?? []
      return similarIssues.map(
        (item: {
          issue: {
            id: number
            node_id: string
            number: number
            title: string
            state: string
            state_reason: string | null
          }
        }) => ({
          id: String(item.issue.id),
          nodeId: item.issue.node_id,
          number: item.issue.number,
          title: item.issue.title,
          state: item.issue.state.toUpperCase(),
          stateReason: item.issue.state_reason?.toUpperCase(),
        }),
      ) as SimilarIssue[]
    },
    enabled: isSimilarIssuesEnabled && isExpandedInternal && isQueryValid,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000, // 1 minute
  })

  // Don't show results when queries are invalid (specifically when users clear input)
  const items = isQueryValid ? (data ?? []) : []

  // Update a single issue in the cache when subscription data arrives
  const updateIssue = useCallback(
    (update: SimilarIssueUpdate) => {
      queryClient.setQueryData<SimilarIssue[]>(queryKey, oldData => {
        if (!oldData) return oldData
        return oldData.map(item => {
          if (item.nodeId !== update.nodeId) return item
          return {
            ...item,
            ...(update.title !== undefined && {title: update.title}),
            ...(update.state !== undefined && {state: update.state}),
            ...(update.stateReason !== undefined && {stateReason: update.stateReason}),
          }
        })
      })
    },
    [queryClient, queryKey],
  )

  // Announce results for screen readers when data changes
  // TODO: check in with a11y team about whether to limit announcements if updates are frequent.
  useEffect(() => {
    if (!isSimilarIssuesEnabled) return
    if (!isFetching && previousCountRef.current !== items.length) {
      previousCountRef.current = items.length
      const count = items.length
      if (count === 0) {
        announce('No similar issues found')
      } else if (count === 1) {
        announce('1 similar issue found')
      } else {
        announce(`${count} similar issues found`)
      }
    }
  }, [isSimilarIssuesEnabled, items.length, isFetching])

  /** Reset state for a new issue creation session when 'Create more' is enabled. Clears section and resets feedback. */
  const resetForNewSession = useCallback(() => {
    setIsExpandedInternal(false)
    setFeedbackResetKey(prev => prev + 1)
    previousCountRef.current = null

    // Clear debounced values so stale results aren't shown
    setDebouncedTitle('')
    setDebouncedBody('')
  }, [])

  return {
    items,
    isFetching,
    isError,
    isExpanded: isExpandedInternal,
    setIsExpanded,
    updateIssue,
    resetForNewSession,
    feedbackResetKey,
  }
}
